import { useState, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import ExitSimulation from '@aytm/app/swidget/base/questions/_common/ExitSimulation.tsx'
import { EcommerceBannerPlacement, IEcommerceQuestionViewProps } from '../types.ts'
import style from './Tesco.module.scss'
import Header from './components/Header.tsx'
import Footer from './components/Footer.tsx'
import CartRail from './components/CartRail.tsx'
import Cart from './components/Cart.tsx'
import Main from './components/Main.tsx'
import Product from './components/Product.tsx'
import useScrollMemory from '../hooks/useScrollMemory.ts'
import useLoadScriptInFrame from '../hooks/useLoadScriptInFrame.ts'

const TescoView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const na = schema.answers.find(answer => answer.na)
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v * cur.price), 0)
  ]
  const selectedAnswer = response.answers.find(r => r.selected)
  const selectedId = selectedAnswer?.id
  const selectedProduct = selectedId ? schema.answers.find(answer => answer.id === selectedId) : undefined
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')

  const [showCart, setShowCart] = useState<boolean>(false)
  const [needHint, setNeedHint] = useState(true)
  // The mobile product page reports when its title has scrolled under the app bar.
  const [productTitleHidden, setProductTitleHidden] = useState<boolean>(false)
  // Kept here, not in the grid, so coming back from a product or the basket lands on the same page.
  const [page, setPage] = useState<number>(1)

  const session = useSession()
  const timeline = useTimeline()
  const theme = useTheme()
  const viewport = useViewport()
  const { containerRef, registerCardRef, findCenterElement, restorePosition, scrollTop, resetCenterElement } =
    useScrollMemory()
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)

  useEffect(() => {
    if (!showCart && !selectedId) {
      restorePosition()
    }
  }, [showCart, selectedId, restorePosition])

  // A new page starts at the top; the card remembered on the old page must not pull the scroll back.
  const onPageChange = (next: number) => {
    setPage(next)
    resetCenterElement()
    scrollTop()
  }

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({ type: 'close_cart' })
    }
    if (selectedId) {
      timeline.log({ type: 'close_popup', ref: selectedId })
    }
    onUpdateResponse({
      answers: response.answers.map(r => ({ ...r, selected: false })),
      clicked: response.clicked
    })
  }

  const onItemSelect = (index: number, bannerPlacement?: EcommerceBannerPlacement) => {
    if (bannerPlacement) {
      timeline.log({
        type: 'banner_click',
        ref: response.answers[index].id,
        context: { placement: bannerPlacement }
      })
    }
    scrollTop()
    if (showCart) {
      setShowCart(false)
      timeline.log({ type: 'close_cart' })
    }
    const answer = response.answers[index]
    answer.selected = true
    timeline.log({ target: '0,0', type: 'open_popup', ref: answer.id })
    onUpdateResponse(response)
  }

  const onItemClose = () => {
    if (!selectedAnswer) return
    timeline.log({ type: 'close_popup', ref: selectedAnswer.id })
    selectedAnswer.selected = false
    onUpdateResponse(response)
  }

  const onCartOpen = () => {
    // The header basket stays clickable on the basket page; a second click must not log open_cart again.
    if (showCart) return
    if (selectedAnswer) {
      timeline.log({ type: 'close_popup', ref: selectedAnswer.id })
      selectedAnswer.selected = false
      onUpdateResponse(response)
    }
    scrollTop()
    setShowCart(true)
    timeline.log({ type: 'open_cart' })
  }

  const onCartClose = () => {
    setShowCart(false)
    timeline.log({ type: 'close_cart' })
  }

  const onAddToCart = (index: number, qnt: number, inCarousel = false) => {
    const answer = response.answers[index]
    answer.v = Math.max(0, answer.v + qnt)
    if (inCarousel || (!showCart && !selectedId)) {
      timeline.log({ target: '0,0', type: 'open_popup', ref: answer.id })
      timeline.log({ type: 'add_to_cart', ref: answer.id, target: '1' })
      timeline.log({ type: 'close_popup', ref: answer.id })
    } else if (showCart) {
      timeline.log({ type: 'edit_in_cart', ref: answer.id, target: answer.v.toString() })
    } else {
      timeline.log({ type: 'add_to_cart', ref: answer.id, target: answer.v.toString() })
      if (findability) {
        timeline.log({ type: 'close_popup', ref: answer.id })
      }
    }
    onUpdateResponse(response)
  }

  const onDeleteFromCart = (index: number) => {
    const answer = response.answers[index]
    answer.v = 0
    timeline.log({ type: 'delete_from_cart', ref: answer.id })
    onUpdateResponse(response)
  }

  const onHomeClick = () => {
    showCart ? onCartClose() : onItemClose()
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse && naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div
        className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`}
        onScroll={findCenterElement}
        ref={containerRef}
      >
        <Header
          schema={schema}
          totalPrice={totals[1]}
          findability={findability}
          clearSelection={clearSelection}
          onCartOpen={onCartOpen}
          onHomeClick={onHomeClick}
          showCart={showCart}
          productOpen={Boolean(selectedProduct)}
          productTitle={selectedProduct && productTitleHidden ? selectedProduct.text : null}
          onItemClose={onItemClose}
        />
        <div className={style.body}>
          {showCart ? (
            <Cart
              schema={schema}
              response={response}
              total={totals[0]}
              totalPrice={totals[1]}
              onCartClose={onCartClose}
              onItemSelect={onItemSelect}
              onAddToCart={onAddToCart}
              onDeleteFromCart={onDeleteFromCart}
              onUpdateResponse={onUpdateResponse}
              checkout={checkout}
            />
          ) : (
            <>
              <div className={style.content}>
                {selectedProduct ? (
                  <Product
                    key={selectedProduct.id}
                    schema={schema}
                    answer={selectedProduct}
                    response={response}
                    onItemClose={onItemClose}
                    onItemSelect={onItemSelect}
                    onAddToCart={onAddToCart}
                    scrollTop={scrollTop}
                    scrollRootRef={containerRef}
                    onTitleHiddenChange={setProductTitleHidden}
                  />
                ) : (
                  <Main
                    schema={schema}
                    response={response}
                    registerCardRef={registerCardRef}
                    onItemSelect={onItemSelect}
                    onAddToCart={onAddToCart}
                    page={page}
                    onPageChange={onPageChange}
                    onBackToTop={scrollTop}
                  />
                )}
              </div>
              {!viewport.mobile && (
                <CartRail
                  schema={schema}
                  response={response}
                  total={totals[0]}
                  totalPrice={totals[1]}
                  findability={findability}
                  selectedId={selectedId}
                  onCartOpen={onCartOpen}
                  onItemSelect={onItemSelect}
                  onItemClose={onItemClose}
                  onAddToCart={onAddToCart}
                  onDeleteFromCart={onDeleteFromCart}
                />
              )}
            </>
          )}
        </div>
        <Footer
          schema={schema}
          totalPrice={totals[1]}
          findability={findability}
          showCart={showCart}
          onCartOpen={onCartOpen}
          onSearchClick={onHomeClick}
          showHint={() => setNeedHint(true)}
          closeSimulation={setClosed}
        />
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default TescoView
